import React, { useEffect, useState, useCallback } from 'react';
import api from '../../api';
import AdminLayout from './AdminLayout';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { toast } from '../../hooks/use-toast';
import { photoSrc } from '../../lib/photos';
import { FileText, Loader2 } from 'lucide-react';

const TABS = [
  { key: 'pending',  label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'all',      label: 'All' },
];

const badge = {
  pending:  'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

export default function AdminDrivers() {
  const [tab, setTab] = useState('pending');
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [viewingDoc, setViewingDoc] = useState(null); // `${driverId}-${docType}` while fetching
  const [rejectTarget, setRejectTarget] = useState(null); // driver_id currently being rejected
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/admin/drivers', { params: { status: tab } });
      setDrivers(data || []);
    } catch (err) {
      toast({ title: 'Error', description: 'Could not load drivers', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const doAction = async (driverId, action) => {
    setBusy(driverId);
    try {
      await api.post(`/api/admin/drivers/${driverId}/${action}`);
      toast({ title: 'Success', description: `Driver ${action}d` });
      load();
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      setBusy(null);
    }
  };

  const openRejectDialog = (driverId) => {
    setRejectReason('');
    setRejectTarget(driverId);
  };

  const submitReject = async () => {
    if (!rejectTarget) return;
    setBusy(rejectTarget);
    try {
      await api.post(`/api/admin/drivers/${rejectTarget}/reject`, { reason: rejectReason.trim() || null });
      toast({ title: 'Driver rejected', description: 'The driver will see this reason next time they try to log in' });
      setRejectTarget(null);
      load();
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      setBusy(null);
    }
  };

  const viewDocument = async (driverId, docType) => {
    const key = `${driverId}-${docType}`;
    setViewingDoc(key);
    // Open the tab synchronously on the click itself — popup blockers in most
    // browsers allow this, but will block a window.open() called after an
    // awaited network request. We navigate this same tab once the blob is ready.
    // (No `noopener` here — we need the window reference to redirect it later.)
    const tab = window.open('', '_blank');
    try {
      const { data, headers } = await api.get(
        `/api/admin/drivers/${driverId}/documents/${docType}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([data], { type: headers['content-type'] || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      if (tab && !tab.closed) {
        tab.location.href = url;
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      tab?.close();
      toast({ title: 'Could not open document', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      setViewingDoc(null);
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Drivers</h1>
          <p className="text-muted-foreground text-sm">Approve or reject driver registrations</p>
        </div>
        <Button variant="outline" onClick={load}>Refresh</Button>
      </div>

      <div className="flex gap-2 mb-4">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`drivers-tab-${t.key}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === t.key ? 'bg-primary-dark text-white' : 'bg-white text-foreground hover:bg-muted border border-border'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground">Loading…</div>
        ) : drivers.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">No drivers in this list.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-background text-left">
              <tr>
                <th className="px-4 py-3 font-semibold">Driver ID</th>
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Phone</th>
                <th className="px-4 py-3 font-semibold">Email</th>
                <th className="px-4 py-3 font-semibold">Experience</th>
                <th className="px-4 py-3 font-semibold">Documents</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {drivers.map((d) => (
                <tr key={d.id} className="border-t border-border" data-testid={`driver-row-${d.driver_id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{d.driver_id}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Avatar className="w-9 h-9 ring-1 ring-border">
                        <AvatarImage src={photoSrc(d.photo_url)} className="object-cover" data-testid={`driver-photo-${d.driver_id}`} />
                        <AvatarFallback className="text-xs">{d.full_name?.[0] || 'D'}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p>{d.full_name}</p>
                        {d.gender && <p className="text-xs text-muted-foreground capitalize">{d.gender}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">{d.phone}</td>
                  <td className="px-4 py-3">{d.email}</td>
                  <td className="px-4 py-3">{d.experience_years} yrs</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-xs justify-start"
                        disabled={!d.has_license_doc || viewingDoc === `${d.driver_id}-license`}
                        onClick={() => viewDocument(d.driver_id, 'license')}
                        data-testid={`view-license-${d.driver_id}`}
                      >
                        {viewingDoc === `${d.driver_id}-license` ? (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <FileText className="w-3 h-3 mr-1" />
                        )}
                        {d.has_license_doc ? 'License' : 'No license'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-xs justify-start"
                        disabled={!d.has_aadhaar_doc || viewingDoc === `${d.driver_id}-aadhaar`}
                        onClick={() => viewDocument(d.driver_id, 'aadhaar')}
                        data-testid={`view-aadhaar-${d.driver_id}`}
                      >
                        {viewingDoc === `${d.driver_id}-aadhaar` ? (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <FileText className="w-3 h-3 mr-1" />
                        )}
                        {d.has_aadhaar_doc ? 'Aadhaar' : 'No Aadhaar'}
                      </Button>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge[d.status] || 'bg-muted text-foreground'}`}>
                      {d.status}
                    </span>
                    {d.status === 'rejected' && d.rejection_reason && (
                      <p className="text-xs text-muted-foreground mt-1 max-w-[200px]" data-testid={`rejection-reason-${d.driver_id}`}>
                        {d.rejection_reason}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    {d.status === 'pending' && (
                      <>
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          disabled={busy === d.driver_id}
                          onClick={() => doAction(d.driver_id, 'approve')}
                          data-testid={`approve-${d.driver_id}`}
                        >
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={busy === d.driver_id}
                          onClick={() => openRejectDialog(d.driver_id)}
                          data-testid={`reject-${d.driver_id}`}
                        >
                          Reject
                        </Button>
                      </>
                    )}
                    {d.status === 'rejected' && (
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        disabled={busy === d.driver_id}
                        onClick={() => doAction(d.driver_id, 'approve')}
                        data-testid={`approve-${d.driver_id}`}
                      >
                        Approve
                      </Button>
                    )}
                    {d.status === 'approved' && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy === d.driver_id}
                        onClick={() => openRejectDialog(d.driver_id)}
                        data-testid={`suspend-${d.driver_id}`}
                      >
                        Suspend
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={!!rejectTarget} onOpenChange={(open) => { if (!open) setRejectTarget(null); }}>
        <DialogContent data-testid="reject-reason-dialog">
          <DialogHeader>
            <DialogTitle>Reject Driver {rejectTarget}</DialogTitle>
            <DialogDescription>
              This reason is shown to the driver the next time they try to log in — be clear so they know what to fix.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g. Aadhaar photo is blurry — please re-upload a clearer scan."
            maxLength={500}
            rows={4}
            data-testid="reject-reason-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)} data-testid="reject-reason-cancel">
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={busy === rejectTarget}
              onClick={submitReject}
              data-testid="reject-reason-submit"
            >
              {busy === rejectTarget ? 'Rejecting…' : 'Reject Driver'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
